/**
 * Imports the necessary modules from the React library for a React component.
 * @module React
 * @module useState
 * @module useTranslation
 */
import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Imports the necessary components and utilities for the current module.
 * @import CustomButton from "../../components/customButton/customButton" - The custom button component.
 * @import ConfirmModal from "../../components/confirmModal/confirmModel" - The confirm modal component.
 * @import TextField from "../../components/TextField/TextField" - The text field component.
 * @import HookForm from "../../components/HookForm/HookForm" - The hook form component.
 * @import Constant from "../../util/constant" - The constant utility module.
 */
import ConfirmModal from "../../components/confirmModal/confirmModel";
import CustomButton from "../../components/customButton/customButton";
import HookForm from "../../components/hookForm/hookForm";
import TextField from "../../components/textField/textField";
import Constant from "../../util/constant";

/**
 * A component that renders a modal for creating genres.
 * @param {{object}} props - The props object containing various properties and functions.
 * @returns The JSX element representing the CreateGenres component.
 */
const CreateGenres = (props) => {
  const { t } = useTranslation();

  /**
   * Destructures the properties from the given props object.
   * @param {{object}} props - The props object containing the properties to destructure.
   * @returns None
   */
  const {
    showCreateGenresModal,
    closeCreateGenresModal,
    modalName,
    createGenresModelLoaderButton,
    handleGenrestextChange,
    placeholder,
    updateGenresModelLoaderButton,
    handleSubmitCreate,
    genrestitletextcount,
    handleGenrestitletextChange,
    titleplaceholder,
    genrestitleValue,
    titlelabel,
    genresdescriptiontextcount,
    handleGenresdescriptiontextChange,
    descriptionplaceholder,
    genresdescriptionValue,
    descriptionlabel,
    label,
    createGenresFunction,
    genresValue,
  } = props;

  /**
   * An object that represents the genres and their validation rules.
   * @property {{string}} gneresname - The genre name.
   * @property {{string}} gnerestitle - The genre title.
   * @property {{string}} gneresdescription - The genre description.
   * @returns None
   */
  const CreateGenres = {
    gneresname: {
      name: "gneresname",
      validate: {
        required: {
          value: true,
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 100,
          message: t("ALLOWED_MAX_100_CHARACTERS"),
        },
      },
    },
    gnerestitle: {
      name: "gnerestitle",
      validate: {
        required: {
          value: false,
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 100,
          message: t("ALLOWED_MAX_100_CHARACTERS"),
        },
      },
    },
    gneresdescription: {
      name: "gneresdescription",
      validate: {
        required: {
          value: false,
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 50,
          message: t("ALLOWED_MAX_50_CHARACTERS"),
        },
      },
    },
  };

  return (
    <ConfirmModal
      className={"Deactivate import-model"}
      modalTitle={modalName.modal === "add" ? t("ADD_GENRE") : t("EDIT_GENRE")}
      showModal={showCreateGenresModal}
      isModelIcon={false}
      modalContent={false}
      isFirstButton={false}
      isSecondButton={false}
      onProcced={() => {}}
      closeModelIcon={() => closeCreateGenresModal()}
      isCloseBtn={true}
    >
      {/**
       * Renders a form component with input fields for creating a genres.
       * @param {Object} defaultForm - The default form values.
       * @param {string} className - The CSS class name for the form.
       * @param {Function} init - The initialization function for the form.
       * @returns The rendered form component.
       */}
      <HookForm
        defaultForm={{
          gneresname: modalName.modal === "add" ? "" : genresValue,
          gnerestitle: modalName.modal === "add" ? "" : genrestitleValue,
          gneresdescription:
            modalName.modal === "add" ? "" : genresdescriptionValue,
        }}
        className="form-horizontal"
        init={(form) => {
          createGenresFunction(form);
        }}
        onSubmit={(e) => {
          handleSubmitCreate(e);
        }}
      >
        {(formMethod) => {
          return (
            <>
              <div className="uploadWrapper createPageWrapper">
                <div className="d-flex">
                  <p className="form-label">{label}</p>
                  <p className="requiredStyle">*</p>
                </div>
                <div className="textFiledWrapper wrapper-input">
                  <TextField
                    formMethod={formMethod}
                    rules={CreateGenres.gneresname.validate}
                    name={CreateGenres.gneresname.name}
                    errors={formMethod?.formState.errors}
                    autoFocus={true}
                    // maxLength={200}
                    // textCountVisible={true}
                    // textcount={genrestextcount}
                    textCountTotal={100}
                    maxLength="100"
                    required={false}
                    type="text"
                    placeholder={placeholder}
                    onChange={handleGenrestextChange}
                  />
                </div>

                <div className="d-flex">
                  <p className="form-label">{titlelabel}</p>
                </div>
                <div className="textFiledWrapper wrapper-input">
                  <TextField
                    formMethod={formMethod}
                    rules={CreateGenres.gnerestitle.validate}
                    name={CreateGenres.gnerestitle.name}
                    errors={formMethod?.formState.errors}
                    autoFocus={false}
                    maxLength="100"
                    // textCountVisible={true}
                    textcount={genrestitletextcount}
                    textCountTotal={100}
                    required={false}
                    type="text"
                    placeholder={titleplaceholder}
                    onChange={handleGenrestitletextChange}
                  />
                </div>

                <div className="d-flex">
                  <p className="form-label">{descriptionlabel}</p>
                </div>
                <div className="textFiledWrapper wrapper-input">
                  <TextField
                    formMethod={formMethod}
                    rules={CreateGenres.gneresdescription.validate}
                    name={CreateGenres.gneresdescription.name}
                    errors={formMethod?.formState.errors}
                    autoFocus={false}
                    maxLength="50"
                    textarea="textarea"
                    // textCountVisible={true}
                    textcount={genresdescriptiontextcount}
                    textCountTotal={50}
                    required={false}
                    type="text"
                    placeholder={descriptionplaceholder}
                    onChange={handleGenresdescriptiontextChange}
                  />
                </div>
              </div>
              <div className="Form_buton">
                <CustomButton
                  className="black-btn fillButton"
                  disabled={
                    modalName?.modal === "add"
                      ? formMethod.getValues("gneresname") === "" ||
                        !formMethod?.formState.isValid
                      : !formMethod?.formState.isDirty ||
                        !formMethod?.formState.isValid ||
                        formMethod.getValues("gneresname") === ""
                  }
                  // disabled={ modalName.modal === "add" ?
                  //   !formMethod?.formState.isValid : !formMethod?.formState.isDirty
                  // }
                  loading={
                    modalName.modal === "add"
                      ? createGenresModelLoaderButton
                      : updateGenresModelLoaderButton
                  }
                  type="submit"
                >
                  {modalName.modal === "add" ? t("ADD_GENERS") : t("UPDATE")}
                </CustomButton>
                <CustomButton
                  className="black-btn borderButton"
                  onClick={() => {
                    closeCreateGenresModal(true);
                  }}
                  type="button"
                >
                  {t("CANCEL")}
                </CustomButton>
              </div>
            </>
          );
        }}
      </HookForm>
    </ConfirmModal>
  );
};

export default CreateGenres;
